import { type Ref } from 'react'
import { Box, Flex, Grid, Text, VStack } from '@chakra-ui/react'
import { LuKeyRound } from 'react-icons/lu'
import {
  useFormColors,
  useFormContext,
  FormSection,
  TrackedInput,
  withFormProvider,
  type WithFormProvider,
} from '@minetrace/ui/forms'
import {
  useStep3AccountForm,
  type Step3AccountFormValues,
} from './useStep3AccountForm'
import { UserFieldLengths } from '../../../lib/fieldLengths'

interface Step3AccountProps {
  ref?: Ref<unknown>
  /** When true, password placeholder reads "deja en blanco para conservar". */
  isEditing?: boolean
}

type InnerProps = Step3AccountProps & WithFormProvider<Step3AccountFormValues>

interface ToggleProps {
  value: boolean
  onChange: (next: boolean) => void
  onLabel: string
  offLabel: string
  onColor: string
}

function Toggle({ value, onChange, onLabel, offLabel, onColor }: ToggleProps) {
  const c = useFormColors()
  return (
    <Flex
      as="button"
      type="button"
      alignItems="center"
      gap={3}
      px={4}
      py={3}
      borderRadius="xl"
      bg={c.bgHover}
      cursor="pointer"
      onClick={() => onChange(!value)}
      transition="all 0.2s"
      _hover={{ bg: c.border }}
      width="fit-content"
    >
      <Box width="44px" height="24px" borderRadius="full" bg={value ? onColor : 'rgba(255,255,255,0.1)'} position="relative" transition="background 0.2s">
        <Box position="absolute" top="2px" left={value ? '22px' : '2px'} width="20px" height="20px" borderRadius="full" bg="white" transition="left 0.2s" boxShadow="0 1px 3px rgba(0,0,0,0.3)" />
      </Box>
      <Text fontSize="sm" color={value ? onColor : c.textMuted} fontWeight="500">
        {value ? onLabel : offLabel}
      </Text>
    </Flex>
  )
}

function Step3InnerComponent({ isEditing = false }: InnerProps) {
  const c = useFormColors()
  const { watch, setValue } = useFormContext<Step3AccountFormValues>()
  const enableUserAccount = watch('enableUserAccount')
  const isExternal = watch('isExternal')
  const hasWebAccess = watch('hasWebAccess')

  return (
    <FormSection
      icon={LuKeyRound}
      titleTid="personas.nuevo.sections.cuenta"
      descriptionTid="personas.nuevo.sections.cuentaDescription"
    >
      <VStack gap={6} alignItems="stretch">
        <Box>
          <Toggle
            value={isExternal}
            onChange={(next) => {
              setValue('isExternal', next)
              setValue('hasWebAccess', !next)
            }}
            onLabel="Usuario externo"
            offLabel="Usuario interno"
            onColor="#3d6fa8"
          />
          <Text fontSize="xs" color={c.textMuted} mt={2}>
            Los usuarios externos representan terceros (conductores, contactos de proveedores) y por defecto solo usan apps móviles.
          </Text>
        </Box>

        <Box>
          <Toggle
            value={enableUserAccount}
            onChange={(next) => setValue('enableUserAccount', next)}
            onLabel="Habilitar cuenta de usuario"
            offLabel="Sin cuenta de usuario"
            onColor={c.accent}
          />
        </Box>

        {enableUserAccount && (
          <>
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
              <TrackedInput name="username" label="Usuario" placeholder="usuario" maxLength={UserFieldLengths.username} />
              <TrackedInput
                name="password"
                label={isEditing ? 'Nueva contraseña' : 'Contraseña'}
                type="password"
                placeholder={isEditing ? 'Dejar en blanco para conservar' : 'Mínimo 6 caracteres'}
                maxLength={128}
              />
            </Grid>

            <Box>
              <Toggle
                value={hasWebAccess}
                onChange={(next) => setValue('hasWebAccess', next)}
                onLabel="Con acceso a la app web"
                offLabel="Sin acceso a la app web"
                onColor="#2a8a87"
              />
              {!hasWebAccess && (
                <Text fontSize="xs" color={c.textMuted} mt={2}>
                  El usuario podrá usar las apps móviles pero no podrá iniciar sesión en la web.
                </Text>
              )}
            </Box>
          </>
        )}

        {!enableUserAccount && (
          <Box p={6} textAlign="center" bg={c.bgHover} borderRadius="xl">
            <Text fontSize="sm" color={c.textMuted}>
              La persona quedará registrada sin credenciales de acceso. Podrás habilitar la cuenta más adelante.
            </Text>
          </Box>
        )}
      </VStack>
    </FormSection>
  )
}

const Step3Inner = withFormProvider(Step3InnerComponent, useStep3AccountForm)

const Step3Account = ({ ref, isEditing, ...rest }: Step3AccountProps & { defaultValues?: Step3AccountFormValues }) => (
  <Step3Inner ref={ref} isEditing={isEditing} {...rest} />
)

export default Step3Account
